// FUENTE ÚNICA del feature-gating (UX, no autorización — el servidor sigue
// siendo la barrera real: RLS, check_plan_limit…).
//
// Desde 2026-09-16 el plan NO gatea features: todas están en todos los planes.
// Lo que el plan define es (1) cuántos tours y cuánto storage —los límites
// duros, en PLAN_CONFIGS + check_plan_limit—, (2) la MARCA (watermark del
// viewer / white-label / branding por tour) y (3) los topes numéricos de
// idiomas y pistas de audio de abajo.
//
// Consumido por: useFeatureGate / <FeatureGate> (React) y usePlanFeature
// (retrocompat). Un test (featureGating.test.ts) bloquea drift.
import type { PlanType } from "@/types/subscription.types";
import { PLAN_LABELS } from "@u/planLabels";

/* ───────────────────────── .360 (rango lineal) ───────────────────────── */

export const PLAN_ORDER: PlanType[] = ["free", "lite", "plus", "pro", "agency"];

/** Ejes de MARCA — lo único que sigue dependiendo del plan (además de los
 *  topes numéricos y de los límites de tours/storage). */
export type PlanFeature = "white_label" | "tour_branding" | "custom_language";

export const FEATURE_PLAN_MAP: Record<PlanFeature, PlanType> = {
  // Viewer sin marca Andar (watermark, colores, loading propio).
  white_label: "pro",
  // Idioma propio del tour (más allá de los 5 de la app) — eje del tope
  // TOUR_LANGUAGE_LIMIT de abajo.
  custom_language: "pro",
  // Branding per-tour (override del branding de cuenta) — tier no self-serve,
  // la sección se OCULTA en vez de mostrar candado (excepción documentada §13).
  tour_branding: "agency",
};

/** Tope de pistas de la playlist de audio ambiente del tour, por plan
 *  (Free/Lite 1, Plus 3, Pro/Agency 5). Lo enforcea el editor
 *  (PlaylistField). */
export const PLAYLIST_TRACK_LIMIT: Record<PlanType, number> = {
  free: 1,
  lite: 1,
  plus: 3,
  pro: 5,
  agency: 5,
};

export function planAllows(
  current: PlanType | undefined | null,
  required: PlanType,
): boolean {
  if (!current) return false;
  return PLAN_ORDER.indexOf(current) >= PLAN_ORDER.indexOf(required);
}

/**
 * How many languages a `.360` tour can offer translated names in, per plan.
 * Numeric cap (not a boolean feature): free 1, lite 3, plus 5, pro/agency 10.
 */
export const TOUR_LANGUAGE_LIMIT: Record<PlanType, number> = {
  free: 1,
  lite: 3,
  plus: 5,
  // Pro/Agency: los 5 idiomas de la app + hasta 5 idiomas propios (Pro-only).
  pro: 10,
  agency: 10,
};

/** Cheapest plan whose language cap covers `count`. */
export function minPlanForTourLanguages(count: number): PlanType {
  return PLAN_ORDER.find((p) => TOUR_LANGUAGE_LIMIT[p] >= count) ?? "pro";
}

/* ───────────────────────── API unificada ─────────────────────────────── */

/** Andar Labs ofrece un solo producto SaaS (.360). El eje queda explícito para
 *  no perder el call-site de cada gate. */
export type GateProduct = "360";

/** Spec del gate (producto + feature). */
export type GateSpec = { product: GateProduct; feature: PlanFeature };

/** Plan efectivo. */
export type AnyPlan = PlanType;

/** ¿El plan efectivo habilita la feature? Resolución pura (sin contexto React). */
export function isFeatureAllowed(spec: GateSpec, plan: AnyPlan): boolean {
  return planAllows(plan, FEATURE_PLAN_MAP[spec.feature]);
}

/** Plan mínimo requerido por la feature (para el CTA del candado). */
export function requiredPlanFor(spec: GateSpec): AnyPlan {
  return FEATURE_PLAN_MAP[spec.feature];
}

/** Una vía de desbloqueo: qué plan habilita la feature. */
export interface UnlockPath {
  product: GateProduct;
  plan: AnyPlan;
}

/** Vías de desbloqueo de una feature (una sola: el plan requerido). */
export function unlockPaths(spec: GateSpec): UnlockPath[] {
  return [{ product: spec.product, plan: requiredPlanFor(spec) }];
}

/** Label de producto para el CTA. */
const PRODUCT_LABELS: Record<GateProduct, string> = { "360": "Andar.360" };
export function productDisplayLabel(product: GateProduct): string {
  return PRODUCT_LABELS[product];
}

/** Label legible del plan para el CTA (lenguaje de producto). Single source =
 *  planLabels.ts (compartido con el demo badge) para que no drifteen. */
// `product` queda en la firma (un solo producto hoy) para no tocar cada
// call-site si vuelve a haber más de uno.
export function planDisplayLabel(product: GateProduct, plan: AnyPlan): string {
  void product;
  return PLAN_LABELS[plan];
}
